// @jnati/phone — the single canonical phone module (#intl-auth-1b).
//
// Before 1b this logic was triplicated byte-for-byte across
// apps/backend/src/utils/phone.ts, apps/web/src/utils/phone.ts, and
// apps/mobile/src/utils/phone.ts. The triplication was the root cause of
// the strip asymmetry that locked out Deepthi (a fix landing in one copy
// and not the others). After 1b there is exactly ONE phone module; every
// import across backend, web, and mobile resolves here.
//
// India-default invariant (the safety basis for shipping before the
// E.164 migration): called with no country argument, every function
// behaves BYTE-IDENTICALLY to the pre-1b India-only code. The DB still
// stores 10-digit national and the phone_number_canonical CHECK stays
// India-only; nothing here yet supplies a non-Indian number end to end.
// The country dropdowns (1c) and the E.164 migration are later dispatches
// that build on this foundation.

import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js'

export type { CountryCode }

// ─────────────────────────────────────────────────────────────────────
// Canonical Indian phone normalization — moved verbatim from
// #phone-norm-1 (backend/utils/phone.ts). UNCHANGED by 1b: storage is
// still 10-digit national, India-only, and this is the sole validator
// that gates how users.phone_number lands in the DB.
//
// Storage format: 10-digit (no country code), starting 6-9 per the
// Indian mobile range (TRAI numbering plan).
//
// Accepted INPUT shapes (anything reducing to a valid 10-digit Indian
// mobile after stripping separators and an optional 91 / +91 / 0 prefix):
//   '+919876543210'  '919876543210'  '09876543210'  '9876543210'
//   '+91 98765 43210'  '(+91) 98765-43210'  '98765 43210'
// REJECTED (throws PhoneValidationError → caller surfaces a 400):
//   '12345'  '5876543210'  '+449876543210'  ''  null  undefined
// ─────────────────────────────────────────────────────────────────────

export class PhoneValidationError extends Error {
  constructor(message = 'Invalid Indian phone format') {
    super(message)
    this.name = 'PhoneValidationError'
  }
}

/**
 * Reduce any accepted input shape to a canonical 10-digit Indian mobile
 * number. Strip whitespace + common separators, peel a leading 91/+91/0
 * prefix, then assert the remainder is exactly 10 digits starting 6-9.
 *
 * India-only by design — the DB still stores national digits. Throws
 * PhoneValidationError on anything that doesn't reduce cleanly.
 */
export function normalizePhone(input: string | null | undefined): string {
  if (typeof input !== 'string' || input.length === 0) {
    throw new PhoneValidationError()
  }
  // Strip everything that isn't a digit (spaces, hyphens, parens, dots,
  // and the leading '+'). After this we have a digit-only string and can
  // peel a country-code or trunk-prefix in one pass.
  const digits = input.replace(/\D/g, '')

  // Peel known leading prefixes: '91' (Indian country code) or '0'
  // (legacy trunk prefix). Anything else is left alone and validated.
  let canonical = digits
  if (canonical.length === 12 && canonical.startsWith('91')) {
    canonical = canonical.slice(2)
  } else if (canonical.length === 11 && canonical.startsWith('0')) {
    canonical = canonical.slice(1)
  }

  // After peeling, must be exactly 10 digits, first digit 6-9 (TRAI
  // mobile range; landlines start 2-5 and are out of scope).
  if (!/^[6-9]\d{9}$/.test(canonical)) {
    throw new PhoneValidationError()
  }
  return canonical
}

// ─────────────────────────────────────────────────────────────────────
// COUNTRIES — the 7 allowlisted countries for 1c's dropdown. Each entry
// carries the libphonenumber ISO code (the validation key — NOT the dial
// code, because US and CA share +1), the E.164 dial code, an emoji flag,
// and a display name.
//
// US/CA both sit on +1: this is an ARRAY, two entries on one dial code.
// Never infer country from dial code alone — use the ISO code. Callers
// that only have a dial code must disambiguate (countriesForDialCode
// returns every match).
// ─────────────────────────────────────────────────────────────────────

export interface CountryConfig {
  /** ISO 3166-1 alpha-2 — the key libphonenumber validates/formats against. */
  iso: CountryCode
  /** E.164 country calling code, e.g. '+91'. Not unique (US/CA share +1). */
  dialCode: string
  /** Emoji flag for the 1c dropdown. */
  flag: string
  /** Human-readable country name for the 1c dropdown. */
  name: string
}

export const COUNTRIES: readonly CountryConfig[] = [
  { iso: 'IN', dialCode: '+91',  flag: '🇮🇳', name: 'India' },
  { iso: 'US', dialCode: '+1',   flag: '🇺🇸', name: 'United States' },
  { iso: 'CA', dialCode: '+1',   flag: '🇨🇦', name: 'Canada' },
  { iso: 'AU', dialCode: '+61',  flag: '🇦🇺', name: 'Australia' },
  { iso: 'GB', dialCode: '+44',  flag: '🇬🇧', name: 'United Kingdom' },
  { iso: 'AE', dialCode: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
  { iso: 'SG', dialCode: '+65',  flag: '🇸🇬', name: 'Singapore' },
] as const

/** The default country for every India-default code path. */
export const DEFAULT_COUNTRY: CountryCode = 'IN'

/** Look up a single allowlisted country by ISO code. */
export function countryByIso(iso: CountryCode): CountryConfig | undefined {
  return COUNTRIES.find((c) => c.iso === iso)
}

/**
 * Every allowlisted country sharing a dial code. Returns an array because
 * +1 maps to BOTH US and CA — callers must disambiguate rather than
 * assume the first match. Empty array if the dial code is not allowlisted.
 */
export function countriesForDialCode(dialCode: string): CountryConfig[] {
  return COUNTRIES.filter((c) => c.dialCode === dialCode)
}

// ─────────────────────────────────────────────────────────────────────
// toE164 — dial-code-aware, India-default.
// ─────────────────────────────────────────────────────────────────────

/**
 * Convert a national number to E.164 for handoff to external systems
 * (Firebase Admin lookups, SMS gateways).
 *
 * India-default: toE164(national) — or toE164(national, 'IN') — takes the
 * exact #phone-norm-1 legacy path and returns `+91` + national after the
 * same `^[6-9]\d{9}$` assertion. BYTE-IDENTICAL to pre-1b; this is the
 * invariant the 35 existing Indian users rely on.
 *
 * For any other allowlisted country, build E.164 via libphonenumber:
 * toE164('412345678', 'AU') → '+61412345678'. (Not yet reachable end to
 * end — the DB CHECK still rejects non-Indian numbers until the migration
 * — but the code path is in place for 1c.)
 *
 * Throws PhoneValidationError on input that isn't a valid number for the
 * given country.
 */
export function toE164(national: string, country: CountryCode = DEFAULT_COUNTRY): string {
  if (country === 'IN') {
    // Legacy path — do NOT route India through libphonenumber. Keeping
    // the original regex guarantees the India-default parity that lets
    // 1b ship before the migration.
    if (!/^[6-9]\d{9}$/.test(national)) {
      throw new PhoneValidationError('toE164 input must be canonical 10-digit')
    }
    return `+91${national}`
  }
  const parsed = parsePhoneNumberFromString(national, country)
  if (!parsed || !parsed.isValid()) {
    throw new PhoneValidationError(`toE164 input is not a valid ${country} number`)
  }
  return parsed.number
}

/**
 * Per-country validity check via libphonenumber. Additive in 1b (nothing
 * yet gates on it) — the 1c dropdown uses it to validate a number against
 * the selected country before handoff. India-default: validate(national)
 * validates against 'IN'.
 *
 * NOTE: this is a separate, stricter validator than normalizePhone (which
 * stays the DB gate). Do not swap normalizePhone for validate — the DB
 * still stores national digits and normalizePhone is its contract.
 */
export function validate(national: string, country: CountryCode = DEFAULT_COUNTRY): boolean {
  if (typeof national !== 'string' || national.length === 0) return false
  const parsed = parsePhoneNumberFromString(national, country)
  return parsed?.isValid() ?? false
}

/**
 * #e164-migration Release C — the TOLERANT write-path canonicalizer (the
 * release invariant, in code). Accepts EITHER format and returns E.164:
 *   - '+919177719333' (old mobile app, new web prep.e164, non-India +61…)
 *      is already E.164 → returned as-is (after stripping separators).
 *   - '9177719333' (bare Indian national, legacy/dependent paths) →
 *      toE164(national, 'IN') → '+919177719333'.
 * Canonicalize, don't reject — so the currently-deployed mobile app (which
 * sends +91… for India) keeps working after storage flips to E.164. Throws
 * PhoneValidationError only on genuinely invalid input; non-India always
 * arrives pre-formatted as E.164, so the national fallback is India-only.
 */
export function toStorageE164(input: string | null | undefined): string {
  if (typeof input === 'string') {
    const stripped = input.replace(/[\s\-().]/g, '')
    if (/^\+[1-9]\d{6,14}$/.test(stripped)) return stripped
  }
  return toE164(normalizePhone(input))
}

/**
 * #e164-migration Release C — masked display for a stored phone. Storage is
 * E.164 now (e.g. '+919177719333' → '+91 91777 XXXXX'); tolerates a legacy
 * bare national too. Fixes the '+91 +9191 XXXXX' bug that came from slicing
 * an E.164 value as if it were national. Multi-country: derives the dial
 * code + national number via libphonenumber, masks the last 5 digits.
 */
export function maskPhone(stored: string | null | undefined): string {
  if (!stored) return ''
  const e164 = stored.startsWith('+') ? stored : `+91${String(stored).replace(/\D/g, '')}`
  const parsed = parsePhoneNumberFromString(e164)
  if (parsed) {
    const nat = parsed.nationalNumber
    const shown = nat.slice(0, Math.max(0, nat.length - 5))
    return `+${parsed.countryCallingCode} ${shown} ${'X'.repeat(nat.length - shown.length)}`.trim()
  }
  // Unparseable fallback — mask the last 5 of the digit run.
  const digits = e164.replace(/\D/g, '')
  return `+${digits.slice(0, Math.max(0, digits.length - 5))} XXXXX`
}

// ─────────────────────────────────────────────────────────────────────
// prepareForSubmit — the one shared "country + typed number → what the
// auth form should do" decision (#intl-auth-1c). Consumed by web + mobile
// so the country logic isn't re-triplicated (the whole point of the
// de-dup). Pure + node-testable (web has no jsdom), so it also carries
// the India-default parity pins and the Deepthi 9177719333 regression.
// ─────────────────────────────────────────────────────────────────────

export type PreparedPhone =
  // Ready to submit. #e164-migration Release C: this now covers ALL 7
  // allowlisted countries (non-India flipped from 'unsupported' to 'ok' once
  // the write path + DB accept E.164).
  | { ok: true; national: string; e164: string }
  // Malformed for the selected country — surface `message`, don't submit.
  | { ok: false; reason: 'invalid'; message: string }

/**
 * Decide what an auth form should do with a typed number + selected
 * country. India (the default) takes the exact pre-1c path — normalizePhone
 * then toE164 — so an Indian submission is byte-identical to before 1c
 * (this is the parity invariant; 9177719333 stays 9177719333 → +919177719333,
 * the Deepthi fix preserved). #e164-migration Release C: other allowlisted
 * countries now also validate + assemble E.164 and return ok — non-India is
 * functional end to end (the write path + DB accept E.164).
 */
export function prepareForSubmit(rawPhone: string, country: CountryCode = DEFAULT_COUNTRY): PreparedPhone {
  const name = countryByIso(country)?.name ?? 'this country'
  if (country === 'IN') {
    let national: string
    try {
      national = normalizePhone(rawPhone)
    } catch {
      return { ok: false, reason: 'invalid', message: 'Enter a valid Indian mobile number' }
    }
    return { ok: true, national, e164: toE164(national) }
  }
  const national = rawPhone.replace(/\D/g, '')
  if (!validate(national, country)) {
    return { ok: false, reason: 'invalid', message: `Enter a valid ${name} mobile number` }
  }
  // #e164-migration Release C — non-India is now FUNCTIONAL: validate +
  // assemble E.164 and return ok. (Was 'unsupported'/coming-soon in 1c.)
  return { ok: true, national, e164: toE164(national, country) }
}
